import { IHierarchicalDropdownOption } from './Select';
import Loader from '@tcl-boron-prefabs/loader';
import cn from 'classnames';
import {
  GetPropsCommonOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetMenuPropsOptions,
  UseSelectGetItemPropsOptions,
  UseSelectGetMenuPropsOptions,
} from 'downshift';
import * as React from 'react';
import { checkIsIndeterminate, checkIsSelected, itemIsSelected, itemToString } from './coreUtils';
import { SelectCheckbox } from './SelectCheckbox';
import { ActionPalette } from '@tcl-boron-colors/colors';
import { MouseEvent } from 'react';
import { CaretDown, CaretRight } from '@tcl-boron-icons/icons';
import { BoldedLabel } from './BoldedLabel/BoldedLabel';
interface HierarchicalMenuProps {
  classes: Record<string, string>;
  isOpen: boolean;
  styles: {
    [key: string]: React.CSSProperties;
  };
  attributes: {
    [key: string]:
      | {
          [key: string]: string;
        }
      | undefined;
  };
  getItemProps: (
    options:
      | UseSelectGetItemPropsOptions<IHierarchicalDropdownOption>
      | UseComboboxGetItemPropsOptions<IHierarchicalDropdownOption>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  items: Array<IHierarchicalDropdownOption>;
  highlightedIndex: number;
  getMenuProps: (
    options?: UseSelectGetMenuPropsOptions | UseComboboxGetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any;
  isSingleSelect: boolean;
  selectedItems: IHierarchicalDropdownOption[];
  inputValue?: string;
  isLoading?: boolean;
  defaultOptionsLabel?: string;
  showTreeStructure?: boolean;
  handleToggleExpand: (checkboxValue: string, isExpanded: boolean) => void;
  rowVirtualizer?: any;
  flattenedStructure: IHierarchicalDropdownOption[];
}

export const HierarchicalMenu: React.ForwardRefExoticComponent<
  HierarchicalMenuProps & React.RefAttributes<HTMLInputElement>
> = React.forwardRef(
  (
    {
      classes,
      isOpen,
      styles,
      attributes,
      getItemProps,
      highlightedIndex,
      items,
      getMenuProps,
      isSingleSelect,
      selectedItems,
      inputValue = '',
      isLoading,
      defaultOptionsLabel,
      showTreeStructure = false,
      handleToggleExpand,
      rowVirtualizer,
      flattenedStructure,
    },
    popperRef,
  ) => {
    const getGrids = () => {
      const initialGrids = rowVirtualizer.virtualItems.map((virtualRow) => {
        const item = items[virtualRow.index];
        const isSelected = itemIsSelected(item, selectedItems);
        const itemProps = getItemProps({
          index: virtualRow.index,
          item: item,
        });

        const singleSelectClearOption = (label: string) => (
          <div className={classes.clearSelectionWrapper}>
            {selectedItems && selectedItems.length > 0 && !inputValue ? (
              <div
                className={cn(
                  classes.flexbox,
                  classes.flexboxWrap,
                  classes.selectCheckboxLabel,
                  classes.selectCheckboxHierarchicalLabel,
                )}
                {...{ ...itemProps }}
              >
                {label}
              </div>
            ) : (
              <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
                <strong>{label}</strong>
              </p>
            )}
          </div>
        );
        const multiSelectClearableButton = (label: string) => (
          <div className={classes.clearSelectionWrapper}>
            <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
              <strong>{label}</strong>
            </p>
            {selectedItems && selectedItems.length > 0 && !inputValue && (
              <button
                {...{ ...itemProps, ref: undefined }}
                className={`${classes.clearSelectionsButton} ${
                  highlightedIndex === virtualRow.index ? classes.clearSelectionsButtonHover : ''
                }`}
              >
                Clear
              </button>
            )}
          </div>
        );
        const divider = <div tabIndex={-1} className={classes.selectedItemsDivider} />;
        const suggestionsText = (
          <p className={cn(classes.selectedText, classes.selectedTextHierarchical, classes.dividerLabel)}>
            <strong>{defaultOptionsLabel}</strong>
          </p>
        );
        return item.value === 'clear' ? (
          <li
            key={item.value}
            className={classes.virtualScrollRows}
            style={{
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '18px',
              backgroundColor:
                isSingleSelect &&
                selectedItems &&
                selectedItems.length > 0 &&
                !inputValue &&
                highlightedIndex === virtualRow.index
                  ? `${ActionPalette[200]}`
                  : 'inherit',
            }}
            ref={virtualRow.measureRef}
          >
            {isSingleSelect && singleSelectClearOption(item.label)}
            {!isSingleSelect && multiSelectClearableButton(item.label)}
          </li>
        ) : item.value === 'divider_and_label' ? (
          <li
            key={item.value}
            className={classes.virtualScrollRows}
            style={{
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '38px',
            }}
            ref={virtualRow.measureRef}
          >
            {divider}
            {suggestionsText}
          </li>
        ) : item.isParentLink ? (
          <li
            key={item.value}
            className={classes.virtualScrollRows}
            style={{
              backgroundColor:
                isSingleSelect && itemIsSelected(items[virtualRow.index - 1], selectedItems)
                  ? `${ActionPalette[400]}`
                  : highlightedIndex === virtualRow.index || highlightedIndex === virtualRow.index - 1
                  ? `${ActionPalette[200]}`
                  : 'inherit',
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '18px',
              cursor: 'pointer',
              textDecoration: highlightedIndex === virtualRow.index ? 'underline' : 'inherit',
              color: ActionPalette[300],
            }}
            ref={virtualRow.measureRef}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            {' '}
            <div
              className={cn(classes.parentLinkButton, {
                [classes.parentLinkButtonMulti]: !isSingleSelect,
                [classes.parentLinkButtonSingle]: isSingleSelect,
              })}
              {...{ ...itemProps }}
            >
              <button className={classes.linkButton}>{item.label}</button>
            </div>
          </li>
        ) : (
          <li
            key={item.value}
            className={classes.virtualScrollRows}
            style={{
              backgroundColor:
                highlightedIndex === virtualRow.index ||
                (highlightedIndex === virtualRow.index + 1 &&
                  (item.hasParent || (item.children && item.children.length > 0)))
                  ? `${ActionPalette[200]}`
                  : 'inherit',
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '24px',
              cursor: 'pointer',
            }}
            ref={virtualRow.measureRef}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            {isSingleSelect && (
              <div
                className={cn(
                  classes.flexbox,
                  classes.flexboxWrap,
                  classes.selectCheckboxLabel,
                  classes.selectCheckboxHierarchicalLabel,
                  {
                    [classes.singleSelectedItemLabel]: isSelected,
                  },
                )}
                {...{ ...itemProps }}
              >
                <BoldedLabel
                  highlightJustBeginning={false}
                  inputValue={inputValue}
                  optionLabel={`${itemToString(item)}`}
                  className={cn(classes.boldedLabel, {
                    [classes.boldedLabelHierarchicalWithLink]: item.hasParent || item.children,
                  })}
                />
              </div>
            )}
            {!isSingleSelect && (
              <div className={`${classes.flexbox} ${classes.flexboxWrap}`}>
                <SelectCheckbox
                  isSelected={isSelected}
                  label={item.label}
                  inputValue={inputValue}
                  item={item}
                  classes={classes}
                  highlightedFromParent={highlightedIndex === virtualRow.index}
                  shouldShowTreeStructure={false}
                  isHierarchical={true}
                  {...{ ...itemProps }}
                />
              </div>
            )}
          </li>
        );
      });
      return initialGrids;
    };
    const getNestedGrids = () => {
      const initialGrids = rowVirtualizer.virtualItems.map((virtualRow) => {
        const item = items[virtualRow.index];
        const isSelected = isSingleSelect
          ? itemIsSelected(item, selectedItems)
          : checkIsSelected(item.value, flattenedStructure);
        const isIndeterminate = checkIsIndeterminate(flattenedStructure, item.value);
        const itemProps = getItemProps({
          index: virtualRow.index,
          item: item,
        });
        const handleToggle = (event: MouseEvent<HTMLDivElement>) => {
          event.preventDefault();
          handleToggleExpand(item.value, !item.isExpanded);
        };
        const backButton = (
          <div
            className={classes.backLinkButton}
            style={{ textDecoration: highlightedIndex === virtualRow.index ? 'underline' : 'inherit' }}
          >
            <button className={classes.linkButton} {...{ ...itemProps, ref: undefined }}>
              Back
            </button>
          </div>
        );
        const parentNameText = (
          <p className={cn(classes.selectedText, classes.selectedTextHierarchical)}>
            <strong>{items[1].label}</strong>
          </p>
        );
        return item.value === 'back_to_results' ? (
          <li
            key={item.value}
            className={`${classes.virtualScrollRows} ${classes.flexbox} ${classes.flexboxWrap}`}
            {...itemProps}
            style={{
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '42px',
            }}
            ref={virtualRow.measureRef}
          >
            {backButton}
            {parentNameText}
          </li>
        ) : (
          <li
            key={item.value}
            className={`${classes.virtualScrollRows} ${classes.flexbox}`}
            style={{
              backgroundColor:
                isSingleSelect && isSelected
                  ? `${ActionPalette[400]}`
                  : highlightedIndex === virtualRow.index
                  ? `${ActionPalette[200]}`
                  : 'inherit',
              transform: `translateY(${virtualRow.start}px)`,
              minHeight: '34px',
              cursor: 'pointer',
            }}
            ref={virtualRow.measureRef}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <div
              className={classes.flexbox}
              style={{ alignItems: 'flex-start' }}
              {...{ ...itemProps, ref: undefined }}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {item.children && item.children.length > 0 && item.isExpanded && (
                <div
                  onClick={(e) => handleToggle(e)}
                  className={classes.flexbox}
                  style={{
                    paddingLeft: `${isSingleSelect ? item.level * 22 : item.level * 26}px`,
                    maxHeight: '34px',
                    width: 'min-content',
                  }}
                >
                  <CaretDown height={10} width={10} className={classes.svgCaret} />
                </div>
              )}
              {item.children && item.children.length > 0 && !item.isExpanded && (
                <div
                  onClick={(e) => handleToggle(e)}
                  className={classes.flexbox}
                  style={{
                    paddingLeft: `${isSingleSelect ? item.level * 22 : item.level * 26}px`,
                    maxHeight: '34px',
                    width: 'min-content',
                  }}
                >
                  <CaretRight height={10} width={10} className={classes.svgCaret} />
                </div>
              )}
              {!isSingleSelect && (
                <SelectCheckbox
                  isSelected={isSelected}
                  isIndeterminate={isIndeterminate}
                  label={item.label}
                  inputValue={inputValue}
                  item={item}
                  classes={classes}
                  style={{ paddingLeft: `${item.children && item.children.length > 0 ? 0 : item.level * 26 + 30}px` }}
                  highlightedFromParent={highlightedIndex === virtualRow.index}
                  shouldShowTreeStructure={true}
                  isHierarchical={true}
                  {...{ ...itemProps }}
                />
              )}
              {isSingleSelect && (
                <div
                  className={cn(
                    classes.selectCheckboxLabel,
                    classes.selectCheckboxHierarchicalLabel,
                    classes.treeStructureSelectedCheckboxLabel,
                    {
                      [classes.singleSelectedItemLabel]: isSelected,
                    },
                  )}
                  {...{ ...itemProps }}
                  style={{ paddingLeft: `${item.children && item.children.length > 0 ? 0 : item.level * 22 + 30}px` }}
                >
                  <BoldedLabel
                    highlightJustBeginning={false}
                    inputValue={inputValue}
                    optionLabel={`${itemToString(item)}`}
                    className={cn(classes.boldedLabel, {
                      [classes.boldedLabelHierarchicalWithLink]: item.hasParent || item.children,
                    })}
                  />
                </div>
              )}
            </div>
          </li>
        );
      });
      return initialGrids;
    };
    const grids = showTreeStructure ? getNestedGrids() : getGrids();
    return (
      <ul
        {...getMenuProps({
          ref: popperRef,
          className: `${classes.menuStyles} ${classes.scrollbar} ${
            isOpen && !isLoading && items.length === 0 ? classes.noResultsFoundMenu : ''
          } ${isOpen && isLoading ? classes.loaderMenu : ''} ${
            !isOpen || (!inputValue && selectedItems.length === 0 && items.length === 0) ? classes.invisibleMenu : ''
          }`,
          style: styles.popper,
          ...(attributes?.popper !== undefined ? attributes.popper : {}),
        })}
      >
        {isOpen && isLoading && (
          <div className={classes.menuLoaderAndNoOp}>
            <Loader size="large" loaderType="secondary" />
          </div>
        )}
        {isOpen && !isLoading && items.length === 0 && (
          <div className={classes.menuLoaderAndNoOp}>No Results Found.</div>
        )}
        {isOpen && !isLoading && items.length > 0 && (
          <>
            <div className={cn(classes.relativeMenuContainerHierarchical)}>
              <li key="total-size" style={{ height: rowVirtualizer.totalSize }} />
              {grids}
            </div>
          </>
        )}
      </ul>
    );
  },
);
HierarchicalMenu.displayName = 'HierarchicalMenu';
